// src/components/ControlloDoppioni.js
// ═══════════════════════════════════════════════════════════════════════════
// «È LA STESSA COSA?» — controllo dei materiali doppi all'inserimento (W408)
// ═══════════════════════════════════════════════════════════════════════════
// useControlloDoppioni() dà a un modulo (voce a mano, NF, richiesta al magazzino) una
// funzione verifica(): prima di salvare legge i materiali già presenti nella commessa da
// tutte le strade e, se qualcosa somiglia, chiede alla persona. Le regole del confronto
// sono in utils/doppioniMateriali.js.
//   const { verifica, finestra } = useControlloDoppioni();
//   const esito = await verifica({ orgId, projectId, righe });
//   if (!esito.procedi) return;            // «Sì, è la stessa: non inserire»
//   … salva con { ...dati, ...esito.campi } // «No, è un'altra cosa»: resta scritto sul documento
//   return <>{…}{finestra}</>;
// Se la lettura non riesce (permessi, rete) il modulo salva come sempre: il controllo aiuta,
// non deve mai impedire il lavoro.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useState } from "react";
import { collection, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { vociMaterialiCommessa, cercaDoppioni, coppieDoppioni, dettaglioVoce } from "../utils/doppioniMateriali";
import { toLocalDateStr } from "../utils/dateUtils";

const leggi = async (col, campo, projectId, orgId) => {
  try {
    const s = await getDocs(query(collection(db, col), where(campo, "==", projectId), where("organizationId", "==", orgId)));
    return s.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch { return []; } // chi non può leggere una collezione: si confronta con il resto
};

/** Materiali della commessa da tutte le strade (ODA, NF, magazzino, voci a mano). */
export async function caricaMaterialiCommessa(orgId, projectId) {
  const [oda, nf, richieste, movimenti] = await Promise.all([
    leggi("ordiniAcquisto", "commessaId", projectId, orgId),
    leggi("nuoveForniture", "commessaId", projectId, orgId),
    leggi("richiesteMateriali", "projectId", projectId, orgId),
    leggi("movimentiEconomici", "projectId", projectId, orgId),
  ]);
  return { oda, nf, richieste, movimenti };
}

const dataIt = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT") : "");
const font = "'Inter',sans-serif";

export function RigaVoce({ voce, piccola = false, prezzi = true }) {
  const dett = dettaglioVoce(voce, { prezzo: prezzi });
  return (
    <div style={{ fontSize: piccola ? 11.5 : 12.5, color: "#374151", lineHeight: 1.45 }}>
      <b>{voce.descrizione}</b>{voce.codice ? <span style={{ color: "#6b7280" }}> · cod. {voce.codice}</span> : null}
      {dett && <span style={{ color: "#6b7280" }}> · {dett}</span>}
      <div style={{ fontSize: 11, color: "#6b7280" }}>
        {voce.etichetta}{voce.stato ? ` (${voce.stato.replace("_", " ")})` : ""}{voce.chi ? ` · ${voce.chi}` : ""}{voce.data ? ` · ${dataIt(voce.data)}` : ""}
      </div>
    </div>
  );
}

function FinestraDoppioni({ trovati, onScegli, prezzi }) {
  const forte = trovati.some(t => t.livello === "forte");
  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => e.target === e.currentTarget && onScegli(false)}>
      <div className="modal" style={{ maxWidth: 520, maxHeight: "88vh", overflowY: "auto", fontFamily: font }}>
        <h3 style={{ fontSize: 15.5, fontWeight: 800, color: forte ? "#b45309" : "#1e1b4b", margin: "0 0 4px" }}>
          ⚠️ {forte ? "Probabile doppione" : "Possibile doppione"}
        </h3>
        <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 12px" }}>In questa commessa c'è già qualcosa di molto simile. È la stessa cosa?</p>
        {trovati.slice(0, 6).map((t, i) => (
          <div key={i} style={{ border: `1px solid ${t.livello === "forte" ? "#fcd34d" : "#e5e7eb"}`, background: t.livello === "forte" ? "#fffbeb" : "#f9fafb", borderRadius: 9, padding: "9px 11px", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#7c3aed", marginBottom: 3 }}>STAI INSERENDO</div>
            <div style={{ fontSize: 12.5, color: "#111827", marginBottom: 6 }}><b>{t.riga.descrizione}</b>{t.riga.quantita ? ` · ${t.riga.quantita}${t.riga.unitaMisura ? " " + t.riga.unitaMisura : ""}` : ""}</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: t.livello === "forte" ? "#b45309" : "#6b7280", marginBottom: 3 }}>C'È GIÀ · {t.motivo}</div>
            <RigaVoce voce={t.voce} prezzi={prezzi} />
          </div>
        ))}
        {trovati.length > 6 && <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 8 }}>e altre {trovati.length - 6} somiglianze</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", marginTop: 6 }}>
          <button type="button" className="btn btn-secondary" onClick={() => onScegli(false)}>Sì, è la stessa: non inserire</button>
          <button type="button" className="btn btn-primary" onClick={() => onScegli(true)}>No, è un'altra cosa: inserisci</button>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>La risposta resta scritta: per queste voci non te lo chiederò più.</div>
      </div>
    </div>
  );
}

// I prezzi delle voci già presenti (ODA, NF, voci a mano) li vede solo chi vede gli importi
// (W415): chi chiede materiale dal cantiere vede cosa c'è già, non quanto è costato.
export function useControlloDoppioni({ mostraPrezzi } = {}) {
  const { currentUser, canSeeEconomic, isPlatformOwner } = useAuth();
  const prezzi = mostraPrezzi ?? !!(canSeeEconomic || isPlatformOwner);
  const [richiesta, setRichiesta] = useState(null); // { trovati, resolve }

  const verifica = useCallback(async ({ orgId, projectId, righe, escludiDoc = null, nonDoppioni = [] }) => {
    const via = { procedi: true, campi: {} };
    if (!orgId || !projectId || !(righe || []).some(r => String(r?.descrizione || "").trim())) return via;
    let trovati = [];
    try {
      const voci = vociMaterialiCommessa(await caricaMaterialiCommessa(orgId, projectId));
      trovati = cercaDoppioni(righe, voci, { data: toLocalDateStr(new Date()), escludiDoc, nonDoppioni });
    } catch { return via; }
    if (!trovati.length) return via;
    return new Promise(resolve => setRichiesta({ trovati, resolve, nonDoppioni }));
  }, []);

  const scegli = (procedi) => {
    if (!richiesta) return;
    const chiavi = [...new Set([...(richiesta.nonDoppioni || []), ...richiesta.trovati.map(t => t.voce.chiave)])]; // con quelle già giudicate
    richiesta.resolve(procedi
      ? { procedi: true, campi: { nonDoppioni: chiavi, nonDoppioniDa: { uid: currentUser?.uid || "", nome: currentUser?.displayName || "", at: serverTimestamp() } } }
      : { procedi: false, campi: {} });
    setRichiesta(null);
  };

  const finestra = richiesta ? <FinestraDoppioni trovati={richiesta.trovati} onScegli={scegli} prezzi={prezzi} /> : null;
  return { verifica, finestra };
}

/**
 * Riquadro dentro un documento che si sta scrivendo (ODA): mostra mentre si compilano le
 * righe se qualcosa è già presente nella commessa. Non blocca: «Non è un doppione» lo segna
 * sul documento (onNonDoppione riceve la chiave della voce).
 */
export function PannelloDoppioni({ orgId, projectId, righe, escludiDoc, nonDoppioni = [], onNonDoppione, puoSegnare = true }) {
  const [voci, setVoci] = useState(null);
  const [segnate, setSegnate] = useState([]);
  React.useEffect(() => {
    let vivo = true;
    setVoci(null);
    if (!orgId || !projectId) return undefined;
    caricaMaterialiCommessa(orgId, projectId).then(d => { if (vivo) setVoci(vociMaterialiCommessa(d)); }).catch(() => { if (vivo) setVoci([]); });
    return () => { vivo = false; };
  }, [orgId, projectId]);
  const trovati = React.useMemo(() => (voci ? cercaDoppioni(righe, voci, { data: toLocalDateStr(new Date()), escludiDoc, nonDoppioni: [...nonDoppioni, ...segnate] }) : []),
    [voci, righe, escludiDoc, nonDoppioni, segnate]);
  if (!trovati.length) return null;
  const segna = async (chiave) => { setSegnate(s => [...s, chiave]); try { await onNonDoppione?.(chiave); } catch { setSegnate(s => s.filter(k => k !== chiave)); } };
  return (
    <div role="region" aria-label="Possibili doppioni" style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderLeft: "4px solid #f59e0b", borderRadius: 9, padding: "10px 12px", marginBottom: 14, fontFamily: font }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#92400e", marginBottom: 6 }}>⚠️ Possibili doppioni in questa commessa ({trovati.length})</div>
      {trovati.slice(0, 8).map((t, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between", padding: "6px 0", borderTop: i ? "1px dashed #fde68a" : "none" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11.5, color: "#7c2d12" }}>«{t.riga.descrizione}» somiglia a ({t.motivo}):</div>
            <RigaVoce voce={t.voce} piccola />
          </div>
          {puoSegnare && <button type="button" onClick={() => segna(t.voce.chiave)}
            style={{ flexShrink: 0, fontSize: 11, padding: "4px 9px", borderRadius: 7, border: "1px solid #fcd34d", background: "#fff", color: "#92400e", fontWeight: 700, cursor: "pointer", fontFamily: font }}>
            Non è un doppione
          </button>}
        </div>
      ))}
    </div>
  );
}

/**
 * Controllo Economico: le coppie sospette già presenti nella commessa (anche vecchie).
 * «Non è un doppione» la toglie per sempre (budgetCommessa.doppioniEsclusi, onEscludi).
 */
export function RiquadroDoppioniCommessa({ orgId, projectId, esclusi = [], aggiorna = 0, puoSegnare = false, onEscludi }) {
  const [voci, setVoci] = useState(null);
  const [segnate, setSegnate] = useState([]);
  const [aperto, setAperto] = useState(true);
  React.useEffect(() => {
    let vivo = true;
    if (!orgId || !projectId) return undefined;
    caricaMaterialiCommessa(orgId, projectId).then(d => { if (vivo) setVoci(vociMaterialiCommessa(d)); }).catch(() => { if (vivo) setVoci([]); });
    return () => { vivo = false; };
  }, [orgId, projectId, aggiorna]);
  const coppie = React.useMemo(() => (voci ? coppieDoppioni(voci, new Set([...esclusi, ...segnate])) : []), [voci, esclusi, segnate]);
  if (!coppie.length) return null;
  const segna = async (chiave) => { setSegnate(s => [...s, chiave]); try { await onEscludi?.(chiave); } catch { setSegnate(s => s.filter(k => k !== chiave)); } };
  return (
    <div role="region" aria-label="Possibili doppioni nei materiali" style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderLeft: "4px solid #f59e0b", borderRadius: 12, padding: "12px 16px", marginBottom: 12, fontFamily: font }}>
      <button type="button" onClick={() => setAperto(a => !a)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: font, textAlign: "left", width: "100%" }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: "#92400e" }}>🔍 Possibili doppioni nei materiali ({coppie.length})</span>
        <span style={{ fontSize: 11.5, color: "#a16207", marginLeft: 8 }}>{aperto ? "▲" : "▼"} stesso materiale arrivato da due strade: controlla che non sia contato due volte</span>
      </button>
      {aperto && coppie.slice(0, 20).map(c => (
        <div key={c.chiave} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12, alignItems: "center", padding: "8px 0", borderTop: "1px dashed #fde68a", marginTop: 6 }}>
          <RigaVoce voce={c.a} piccola />
          <RigaVoce voce={c.b} piccola />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: c.livello === "forte" ? "#b45309" : "#6b7280", whiteSpace: "nowrap" }}>{c.livello === "forte" ? "PROBABILE" : "POSSIBILE"} · {c.motivo}</span>
            {puoSegnare && <button type="button" onClick={() => segna(c.chiave)}
              style={{ fontSize: 11, padding: "4px 9px", borderRadius: 7, border: "1px solid #fcd34d", background: "#fff", color: "#92400e", fontWeight: 700, cursor: "pointer", fontFamily: font, whiteSpace: "nowrap" }}>
              Non è un doppione
            </button>}
          </div>
        </div>
      ))}
      {aperto && coppie.length > 20 && <div style={{ fontSize: 11.5, color: "#92400e", marginTop: 6 }}>e altre {coppie.length - 20}</div>}
      {aperto && <div style={{ fontSize: 11, color: "#a16207", marginTop: 8 }}>Se è davvero lo stesso materiale, togli o correggi una delle due voci dalla sua sezione (ODA, NF, richiesta al magazzino o voce a mano).</div>}
    </div>
  );
}
